import { IntegrationPartner } from "refsix-js-models";

import catalogue from "./assignmentPlatforms.json";

/**
 * The assignment platforms REFSIX imports from by scraping, because they have no
 * public API (REFSIX-568).
 *
 * This is the single source of truth for the set of platforms, shared by:
 *
 *  - the phone app — which tiles appear in Integrations, and the per-platform page;
 *  - refauth — `features/integrations/assignments/platforms.js`, which adds the
 *    mapping detail (misconduct codes, sin bin, match formats) it needs to turn a
 *    scraped fixture into a match;
 *  - the adapters in `./assignments/adapters`, which do the scraping. They are a
 *    leaf that imports nothing from the rest of `core` — they are bundled into a
 *    script injected into a WebView, and core's own bundle carries d3, xlsx,
 *    moment-timezone and pako. `adapters/catalogue.test.ts` asserts parity with
 *    this list, so a platform cannot be half-added.
 *
 * Adding a platform is an entry here, an adapter next to it, and a mapping entry
 * in refauth. The parity test fails if any of the three is missed.
 */
export interface AssignmentPlatform {
    /**
     * Stable id. Must equal the adapter's id and the key in refauth's MAPPINGS.
     * Also the `source` recorded on every imported match.
     *
     * Typed as the enum so the JSON cannot drift from `IntegrationPartner` —
     * the adapters themselves declare it as a plain string, because they are
     * bundled into an injected script and must not import `models`.
     */
    id: IntegrationPartner;

    /** As the platform names itself, shown to the user. */
    displayName: string;

    /**
     * Prefix for the `_id` of matches imported from this platform. How an
     * already-imported fixture is recognised on a re-import, so it must never
     * change once live: doing so would orphan every match a user has imported and
     * re-add them as duplicates.
     */
    idPrefix: string;

    /**
     * Whether the platform's tile is shown. Not a per-user or per-subscription
     * grant: there is nothing to grant, since the scrape reads the referee's own
     * session.
     *
     * It was a dogfooding gate (unlaunched platforms appeared only under
     * developer tools) and is now a kill switch. That is the reason to keep it:
     * withdrawing a platform after a store release — a scrape that breaks, a
     * platform that objects — otherwise needs a build, and a build needs review.
     */
    launched: boolean;

    /** Match patterns for the platform's own pages, in manifest syntax. */
    hostPatterns: string[];

    /** Where to send the user to find their schedule. */
    scheduleUrl: string;

    /** Whether a scrape can see the other appointed officials. */
    importsOfficials: boolean;

    /** Whether a scrape can see the match fee. */
    importsFees: boolean;

    /**
     * Whether the phone app can import this platform in an in-app WebView.
     *
     * Since the browser extension was removed this is the only import path, so
     * a platform with this false has no route at all and should not be
     * `launched`.
     *
     * Not a property of the scrape — the adapter is the same either way — but of
     * the platform's sign-in: Google and Microsoft both refuse OAuth from an
     * embedded WebView (`disallowed_useragent`), so a platform whose only login
     * is federated cannot be imported this way however good the adapter is. It
     * A platform whose ONLY sign-in is federated cannot be imported this way
     * however good the adapter is. Where only some routes are federated, say so
     * with `unsupportedSignIn` rather than turning this off.
     */
    inAppImport: boolean;

    /**
     * A sign-in provider this platform offers that cannot complete inside a
     * WebView, named as the user sees it ("Google"). Absent when every route
     * works.
     *
     * Arbiter signs in through Azure AD B2C (tenant
     * arbitersportsb2c.onmicrosoft.com, policy B2C_1A_DefaultV1_Signin) and
     * offers both an email/password local account and "Sign in with Google".
     * The local account works: the `disallowed_useragent` refusal belongs to
     * accounts.google.com and to Microsoft's consumer/work login, not to B2C,
     * which is a white-label directory with no third-party account to protect.
     * Choosing Google leaves B2C for accounts.google.com, which refuses
     * embedded WebViews outright — so the platform page tells the referee to
     * use their email and password instead.
     *
     * Not translated: a provider's name is its name in every language.
     */
    unsupportedSignIn?: string;
}

export const ASSIGNMENT_PLATFORMS: AssignmentPlatform[] =
    catalogue as AssignmentPlatform[];

export function assignmentPlatform(
    id: IntegrationPartner | string | undefined
): AssignmentPlatform | undefined {
    return ASSIGNMENT_PLATFORMS.find((platform) => platform.id === id);
}

export function assignmentPlatformIds(): IntegrationPartner[] {
    return ASSIGNMENT_PLATFORMS.map((platform) => platform.id);
}

export function isAssignmentPlatform(
    id: IntegrationPartner | string | undefined
): boolean {
    return !!assignmentPlatform(id);
}

/**
 * The `_id` prefix for a platform's matches, or undefined for an unknown one.
 * Callers that build document ids should treat undefined as "refuse", never as
 * "no prefix" — an unprefixed id would collide with the user's own matches.
 */
export function assignmentPlatformIdPrefix(
    id: IntegrationPartner | string | undefined
): string | undefined {
    return assignmentPlatform(id)?.idPrefix;
}
